import { Stack, Typography } from '@mui/material';
import { XIcon } from '@phosphor-icons/react';
import { getRouteApi, linkOptions } from '@tanstack/react-router';
import { Popup, useMap } from '@vis.gl/react-maplibre';
import type { Point } from 'geojson';
import type { ReactNode } from 'react';
import { useCallback, useEffect } from 'react';

import { StyledDivider } from '@/components/common/StyledDivider';
import { MapLayerIcon } from '@/components/display/MapLayerIcon';
import { ButtonLink } from '@/components/router/ButtonLink';
import { IconButtonLink } from '@/components/router/IconButtonLink';
import { useMapOnEvent } from '@/hooks/useMapOnEvent';
import { useScreenSize } from '@/hooks/useScreenSize';
import { MAP_FLY_TO_ZOOM } from '@/modules/MAP_FLY_TO_ZOOM';
import { PointGuard } from '@/modules/PointGuard';
import { FeatureType } from '@/types/FeatureType';
import type { MapLayer } from '@/types/MapLayer';

const route = getRouteApi('/_public/_map');

interface DetailCardProps {
  id: string;
  type: FeatureType;
  mapLayer: MapLayer;
  point: Point;
  title: string;
  subTitle?: string;
  children: ReactNode;
}

export function DetailPopup({ id, type, mapLayer, point, title, subTitle, children }: DetailCardProps) {
  const navigate = route.useNavigate();
  const mapRef = useMap();
  const { isDesktop } = useScreenSize();

  const flyToFeature = useCallback(() => {
    if (!mapRef.current || !PointGuard.hasLongitudeAndLatitude(point)) return;

    const [lng, lat] = point.coordinates;
    mapRef.current.flyTo({
      center: [lng, lat],
      zoom: MAP_FLY_TO_ZOOM - 2,
      offset: [0, isDesktop ? 0 : -175],
      duration: 400,
    });
  }, [mapRef, point, isDesktop]);

  useEffect(() => {
    if (mapRef.current?.isStyleLoaded()) {
      flyToFeature();
    }
  }, [mapRef, flyToFeature]);

  useMapOnEvent({
    type: 'load',
    listener: flyToFeature,
    enabled: !!point,
  });

  if (!point || !PointGuard.hasLongitudeAndLatitude(point)) return null;

  const handleClose = async () => navigate({ search: (prev) => ({ ...prev, type: undefined, id: undefined }) });

  const detailCard = (
    <Stack bgcolor="background.paper" borderRadius={2} boxShadow={2} minWidth={{ xs: 'unset', lg: '420px' }}>
      <Stack alignItems="flex-start" direction="row" gap={2} p={2}>
        <MapLayerIcon mapLayer={mapLayer} />
        <Stack alignItems="flex-start" direction="row" justifyContent="space-between" width="100%">
          <Stack>
            <Typography variant="h2">{title}</Typography>
            {subTitle ? (
              <Typography color="textSecondary" variant="body2">
                {subTitle}
              </Typography>
            ) : null}
          </Stack>
          <IconButtonLink
            disableFocusRipple
            from="/"
            onClick={handleClose}
            search={(search) => ({ ...search, type: undefined, id: undefined })}
            size="medium"
            sx={{ '& .MuiIconButton-root': { borderRadius: 0 }, borderRadius: 0 }}
          >
            <XIcon />
          </IconButtonLink>
        </Stack>
      </Stack>

      <StyledDivider variant="middle" />

      <Stack px={2} py={1.5}>
        {children}
      </Stack>

      <StyledDivider variant="middle" />

      <Stack alignItems="flex-end" p={2}>
        <ButtonLink
          color="primary"
          from="/"
          search={(prev) => ({ ...prev, id: undefined, type: undefined })}
          variant="contained"
          {...(type === FeatureType.PARKING_FACILITY
            ? linkOptions({
                to: '/parking-facilities/$parkingFacilityId',
                params: { parkingFacilityId: id },
              })
            : {})}
          {...(type === FeatureType.STATION
            ? linkOptions({
                to: '/stations/$stationId',
                params: { stationId: id },
              })
            : {})}
          {...(type === FeatureType.PARK_AND_RIDE_FACILITY
            ? linkOptions({
                to: '/park-and-ride-facilities/$parkAndRideFacilityId',
                params: { parkAndRideFacilityId: id },
              })
            : {})}
          {...(type === FeatureType.PICKUP_PARKING_FACILITY
            ? linkOptions({
                to: '/pickup-parking-facilities/$pickupParkingFacilityId',
                params: { pickupParkingFacilityId: id },
              })
            : {})}
        >
          Mehr
        </ButtonLink>
      </Stack>
    </Stack>
  );

  return isDesktop ? (
    <Popup
      anchor="top-left"
      closeButton={false}
      closeOnClick={false}
      latitude={point.coordinates[1]}
      longitude={point.coordinates[0]}
      offset={32}
      onClose={handleClose}
    >
      {detailCard}
    </Popup>
  ) : (
    <Stack
      sx={{
        position: 'fixed',
        width: '100%',
        bottom: 0,
        zIndex: 10,
      }}
    >
      {detailCard}
    </Stack>
  );
}
